/**
 * Cache Invalidation Strategy Implementation
 * Provides comprehensive cache invalidation mechanisms for React Query and localStorage
 */

import { QueryClient } from '@tanstack/react-query';
import { trackingKeys } from '../hooks/queries/useTrackingData';
import { campaignStatsKeys, CAMPAIGN_STATS_PROVIDERS } from '../hooks/queries/useCampaignStats';
import { clearAllCache, removeFromCache, getCacheStats } from './localStorageCache';

// Cache invalidation event types
export type CacheInvalidationEvent = 
  | 'campaign_updated'
  | 'email_sent'
  | 'user_profile_changed'
  | 'manual_refresh'
  | 'app_version_changed'
  | 'schema_migration'
  | 'api_error_recovery'
  | 'data_merged'
  | 'partial_update';

// Cache freshness levels
export enum CacheFreshness {
  FRESH = 'fresh',      // < 5 minutes
  RECENT = 'recent',    // 5-30 minutes  
  STALE = 'stale',      // 30 minutes - 24 hours
  EXPIRED = 'expired'   // > 24 hours
}

// Configuration for different data types
const CACHE_FRESHNESS_CONFIG = {
  tracking: {
    fresh: 5 * 60 * 1000,      // 5 minutes
    recent: 30 * 60 * 1000,    // 30 minutes
    stale: 24 * 60 * 60 * 1000 // 24 hours
  },
  campaignStats: {
    fresh: 15 * 60 * 1000,     // 15 minutes
    recent: 60 * 60 * 1000,    // 1 hour
    stale: 24 * 60 * 60 * 1000 // 24 hours
  },
  userProfile: {
    fresh: 60 * 60 * 1000,     // 1 hour
    recent: 4 * 60 * 60 * 1000, // 4 hours
    stale: 24 * 60 * 60 * 1000  // 24 hours
  }
} as const;

// Cache version tracking
const CACHE_VERSION_KEY = 'cache_version';
const CURRENT_CACHE_VERSION = '1.1.0';

/**
 * Cache Invalidation Manager
 * Centralized management of cache invalidation strategies
 */
export class CacheInvalidationManager {
  private queryClient: QueryClient;
  private invalidationListeners: Map<CacheInvalidationEvent, Array<() => void>> = new Map();

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
    this.initializeVersionTracking();
  }

  /**
   * Initialize cache version tracking and handle migrations
   */
  private initializeVersionTracking(): void {
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    
    if (!storedVersion || storedVersion !== CURRENT_CACHE_VERSION) {
      console.log(`Cache version mismatch. Stored: ${storedVersion}, Current: ${CURRENT_CACHE_VERSION}`);
      this.handleCacheVersionMigration(storedVersion, CURRENT_CACHE_VERSION);
      localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);
    }
  }

  /**
   * Handle cache version migrations
   */
  private handleCacheVersionMigration(oldVersion: string | null, newVersion: string): void {
    console.log('Performing cache migration...');
    
    // For now, clear all cache on version changes
    // In the future, we could implement more sophisticated migration logic
    this.clearAllCaches();
    
    this.triggerEvent('schema_migration');
  }

  /**
   * Get cache freshness level for a query
   */
  getCacheFreshness(queryKey: readonly unknown[], dataType: keyof typeof CACHE_FRESHNESS_CONFIG): CacheFreshness {
    const queryData = this.queryClient.getQueryData(queryKey);
    const queryState = this.queryClient.getQueryState(queryKey);
    
    if (!queryData || !queryState) {
      return CacheFreshness.EXPIRED;
    }

    const now = Date.now();
    const dataUpdatedAt = queryState.dataUpdatedAt || 0;
    const age = now - dataUpdatedAt;

    const config = CACHE_FRESHNESS_CONFIG[dataType];

    if (age < config.fresh) return CacheFreshness.FRESH;
    if (age < config.recent) return CacheFreshness.RECENT;
    if (age < config.stale) return CacheFreshness.STALE;
    return CacheFreshness.EXPIRED;
  }

  /**
   * Time-based invalidation with tiered freshness
   */
  invalidateByFreshness(dataType: keyof typeof CACHE_FRESHNESS_CONFIG, minFreshness: CacheFreshness): void {
    const queries = this.queryClient.getQueryCache().getAll();
    
    queries.forEach(query => {
      const freshness = this.getCacheFreshness(query.queryKey, dataType);
      
      // If data is less fresh than required, invalidate it
      const freshnessOrder = [CacheFreshness.FRESH, CacheFreshness.RECENT, CacheFreshness.STALE, CacheFreshness.EXPIRED];
      const currentIndex = freshnessOrder.indexOf(freshness);
      const requiredIndex = freshnessOrder.indexOf(minFreshness);
      
      if (currentIndex >= requiredIndex) {
        this.queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
    });
  }

  /**
   * Event-based cache invalidation
   */
  invalidateByEvent(event: CacheInvalidationEvent, campaignId?: string): void {
    console.log(`Cache invalidation triggered by event: ${event}`, campaignId ? `for campaign: ${campaignId}` : '');

    switch (event) {
      case 'email_sent':
        // Invalidate campaign list and specific campaign tracking
        this.queryClient.invalidateQueries({ queryKey: trackingKeys.campaigns() });
        if (campaignId) {
          this.queryClient.invalidateQueries({ queryKey: trackingKeys.campaign(campaignId) });
          this.queryClient.invalidateQueries({ queryKey: campaignStatsKeys.campaignBase(campaignId) });
        }
        break;

      case 'campaign_updated':
        // Invalidate specific campaign data and related stats
        if (campaignId) {
          this.queryClient.invalidateQueries({ queryKey: trackingKeys.campaign(campaignId) });
          this.queryClient.invalidateQueries({ queryKey: campaignStatsKeys.campaignBase(campaignId) });
          // Also invalidate campaign list to reflect changes
          this.queryClient.invalidateQueries({ queryKey: campaignStatsKeys.all });
        }
        break;

      case 'user_profile_changed':
        // Invalidate user-specific data
        this.queryClient.invalidateQueries({ queryKey: ['userProfile'] });
        this.queryClient.invalidateQueries({ queryKey: ['userSettings'] });
        break;

      case 'manual_refresh':
        // Force refresh all data
        this.queryClient.invalidateQueries();
        break;

      case 'app_version_changed':
      case 'schema_migration':
        // Clear all caches for major changes
        this.clearAllCaches();
        break;

      case 'api_error_recovery':
        // Invalidate potentially stale data after API recovery
        this.invalidateByFreshness('tracking', CacheFreshness.RECENT);
        this.invalidateByFreshness('campaignStats', CacheFreshness.RECENT);
        break;
    }

    // Trigger registered listeners
    this.triggerEvent(event);
  }

  /**
   * Selective invalidation by campaign ID with cascading effects
   */
  invalidateCampaign(campaignId: string, cascade: boolean = true): void {
    // Remove specific campaign data
    this.queryClient.removeQueries({ queryKey: [...trackingKeys.campaign(campaignId)] });
    this.queryClient.removeQueries({ queryKey: [...campaignStatsKeys.campaignBase(campaignId)] });
    
    // Remove from localStorage
    removeFromCache([...trackingKeys.campaign(campaignId)]);
    CAMPAIGN_STATS_PROVIDERS.forEach(provider => {
      removeFromCache([...campaignStatsKeys.campaign(campaignId, provider)]);
    });

    if (cascade) {
      // Invalidate related data that might reference this campaign
      this.queryClient.invalidateQueries({ queryKey: trackingKeys.campaigns() });
      this.queryClient.invalidateQueries({ queryKey: campaignStatsKeys.all });
    }
  }

  /**
   * Selective invalidation with pattern matching
   */
  invalidateByPattern(pattern: string): void {
    const queries = this.queryClient.getQueryCache().getAll();
    
    queries.forEach(query => {
      const queryKeyString = JSON.stringify(query.queryKey);
      if (queryKeyString.includes(pattern)) {
        this.queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
    });
  }

  /**
   * Background refetching for stale data
   */
  backgroundRefreshStaleData(): void {
    const queries = this.queryClient.getQueryCache().getAll();
    
    queries.forEach(query => {
      const queryState = this.queryClient.getQueryState(query.queryKey);
      if (queryState && queryState.status === 'success' && queryState.dataUpdatedAt) {
        const now = Date.now();
        const age = now - queryState.dataUpdatedAt;
        
        // Use a default stale time of 5 minutes if not specified
        const staleTime = 5 * 60 * 1000; // 5 minutes default
        const isStale = age > staleTime;
        
        if (isStale) {
          // Refetch in background without affecting UI
          this.queryClient.refetchQueries({ 
            queryKey: query.queryKey,
            type: 'active' // Only refetch if query is currently being used
          });
        }
      }
    });
  }

  /**
   * Clear all caches (React Query + localStorage)
   */
  clearAllCaches(): void {
    this.queryClient.clear();
    clearAllCache();
    console.log('All caches cleared');
  }

  /**
   * Get cache status and statistics
   */
  getCacheStatus() {
    const queryCache = this.queryClient.getQueryCache();
    const queries = queryCache.getAll();
    const localStorageStats = getCacheStats();

    const queryStats = {
      total: queries.length,
      fresh: 0,
      stale: 0,
      inactive: 0,
      error: 0
    };

    queries.forEach(query => {
      const state = query.state;
      if (state.status === 'error') queryStats.error++;
      else if (state.status === 'success') {
        // Check if data is stale
        const now = Date.now();
        const age = now - (state.dataUpdatedAt || 0);
        const staleTime = 5 * 60 * 1000; // 5 minutes default
        const isStale = age > staleTime;
        
        if (isStale) queryStats.stale++;
        else queryStats.fresh++;
      }
      
      if (!query.getObserversCount()) queryStats.inactive++;
    });

    return {
      reactQuery: queryStats,
      localStorage: localStorageStats,
      version: CURRENT_CACHE_VERSION
    };
  }

  /**
   * Register event listener for cache invalidation events
   */
  onInvalidation(event: CacheInvalidationEvent, callback: () => void): () => void {
    if (!this.invalidationListeners.has(event)) {
      this.invalidationListeners.set(event, []);
    }
    
    this.invalidationListeners.get(event)!.push(callback);
    
    // Return unsubscribe function
    return () => {
      const listeners = this.invalidationListeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  /**
   * Trigger event listeners
   */
  private triggerEvent(event: CacheInvalidationEvent): void {
    const listeners = this.invalidationListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error(`Error in cache invalidation listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Prefetch data for better UX
   */
  prefetchCampaignData(campaignId: string): void {
    // Prefetch both tracking and stats data
    this.queryClient.prefetchQuery({
      queryKey: trackingKeys.campaign(campaignId),
      staleTime: 30 * 1000, // Consider fresh for 30 seconds
    });

    CAMPAIGN_STATS_PROVIDERS.forEach(provider => {
      this.queryClient.prefetchQuery({
        queryKey: campaignStatsKeys.campaign(campaignId, provider),
        staleTime: provider === 'ses' ? 60 * 1000 : 5 * 60 * 1000, // SES stats evaluated more frequently
      });
    });
  }

  /**
   * Optimistic updates with automatic validation
   */
  optimisticUpdate<T>(
    queryKey: readonly unknown[], 
    updater: (oldData: T | undefined) => T,
    rollbackOnError: boolean = true
  ): void {
    const previousData = this.queryClient.getQueryData<T>(queryKey);
    
    // Apply optimistic update
    this.queryClient.setQueryData(queryKey, updater);
    
    if (rollbackOnError) {
      // Schedule background validation
      setTimeout(() => {
        this.queryClient.invalidateQueries({ queryKey })
          .catch(() => {
            // Rollback on validation failure
            this.queryClient.setQueryData(queryKey, previousData);
          });
      }, 1000);
    }
  }
}

// Global cache invalidation manager instance
let cacheManager: CacheInvalidationManager | null = null;

/**
 * Initialize the global cache invalidation manager
 */
export const initializeCacheInvalidation = (queryClient: QueryClient): CacheInvalidationManager => {
  if (!cacheManager) {
    cacheManager = new CacheInvalidationManager(queryClient);
  }
  return cacheManager;
};

/**
 * Get the global cache invalidation manager
 */
export const getCacheInvalidationManager = (): CacheInvalidationManager => {
  if (!cacheManager) {
    throw new Error('Cache invalidation manager not initialized. Call initializeCacheInvalidation first.');
  }
  return cacheManager;
};

// Convenience functions for common operations
export const invalidateTrackingData = (campaignId?: string) => {
  const manager = getCacheInvalidationManager();
  if (campaignId) {
    manager.invalidateCampaign(campaignId);
  } else {
    manager.invalidateByEvent('manual_refresh');
  }
};

export const invalidateOnEmailSent = (campaignId: string) => {
  const manager = getCacheInvalidationManager();
  manager.invalidateByEvent('email_sent', campaignId);
};

export const invalidateOnCampaignUpdate = (campaignId: string) => {
  const manager = getCacheInvalidationManager();
  manager.invalidateByEvent('campaign_updated', campaignId);
};
